import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactModal from 'react-modal';
import { toast, ToastContainer } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';

import 'react-toastify/dist/ReactToastify.css';

if (typeof document !== 'undefined') {
  ReactModal.setAppElement('#root');
}

export default function Timeline() {
  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const queryClient = useQueryClient();

  /* ===== Fetch history ===== */
  const { data: historyResp } = useQuery({
    queryKey: ['familyHistorySettings'],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-history-settings-api`, { withCredentials: true })
        .then(res => res.data),
    staleTime: 0,
    cacheTime: Infinity,
  });

  /* ===== Local state ===== */
  const [historyList, setHistoryList] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (historyResp?.success && !hydrated) {
      setHistoryList(historyResp.data || []);
      setHydrated(true);
    }
  }, [historyResp, hydrated]);

  /* ===== Add story ===== */
  const [addOpen, setAddOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [year, setYear] = useState('');
  const [eventDate, setEventDate] = useState('');

  const [imageFiles, setImageFiles] = useState([]);
const [imagePreviews, setImagePreviews] = useState([]);

  const [videoFiles, setVideoFiles] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const triggerImagePicker = () => imageInputRef.current?.click();
  const triggerVideoPicker = () => videoInputRef.current?.click();

  /*const handleChooseImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const nextFiles = [...imageFiles, ...files];
    imagePreviews.forEach(u => URL.revokeObjectURL(u));
    const nextPreviews = nextFiles.map(f => URL.createObjectURL(f));
    setImageFiles(nextFiles);
    setImagePreviews(nextPreviews);
  };*/

  /*const handleChooseImages = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
  };*/

  
  const handleChooseImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
  
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    };
  
    try {
      // Compress all images selected
      const compressedFiles = await Promise.all(
        files.map(async (file) => {
          const compressed = await imageCompression(file, options);
          return new File([compressed], file.name, { type: compressed.type });
        })
      );
  
      // Append new compressed files to existing ones
      const nextFiles = [...imageFiles, ...compressedFiles];
  
      // Cleanup old preview URLs to avoid memory leaks
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
  
      // Generate preview URLs for compressed files
      const nextPreviews = nextFiles.map((file) =>
        URL.createObjectURL(file)
      );
  
      setImageFiles(nextFiles);
      setImagePreviews(nextPreviews);
  
      // Reset input value so selecting same file again triggers event
      e.target.value = null;
  
    } catch (err) {
      console.error("Image compression error:", err);
      toast.error("Failed to compress selected images.");
    }
  };
  

  const handleChooseVideos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const nextFiles = [...videoFiles, ...files];
    videoPreviews.forEach(u => URL.revokeObjectURL(u));
    const nextPreviews = nextFiles.map(f => URL.createObjectURL(f));
    setVideoFiles(nextFiles);
    setVideoPreviews(nextPreviews);
  };

  const clearAllImages = () => {
    imagePreviews.forEach(u => URL.revokeObjectURL(u));
    setImageFiles([]);
    setImagePreviews([]);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };
  const clearAllVideos = () => {
    videoPreviews.forEach(u => URL.revokeObjectURL(u));
    setVideoFiles([]);
    setVideoPreviews([]);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const removeImageAt = (idx) => {
    setImageFiles(prev => prev.filter((_, i) => i !== idx));
    setImagePreviews(prev => {
      const copy = [...prev];
      const removed = copy[idx];
      if (removed) URL.revokeObjectURL(removed);
      copy.splice(idx, 1);
      return copy;
    });
  };
  const removeVideoAt = (idx) => {
    setVideoFiles(prev => prev.filter((_, i) => i !== idx));
    setVideoPreviews(prev => {
      const copy = [...prev];
      const removed = copy[idx];
      if (removed) URL.revokeObjectURL(removed);
      copy.splice(idx, 1);
      return copy;
    });
  };




  const [memoryList, setMemoryList] = useState([]);

const {
  data: memoryData,
  isLoading,
  isError
} = useQuery({
  queryKey: ['familyMemories'],
  queryFn: () =>
    axios.get(`${API_URL}/get-family-memories`, { withCredentials: true })
      .then(res => res.data),
  staleTime: 0,
});

useEffect(() => {
  if (memoryData?.success) {
    setMemoryList(memoryData.data);
  }
}, [memoryData]);


  const [savingAdd, setSavingAdd] = useState(false);
  const [expandedMemories, setExpandedMemories] = useState({});


  const handleSaveNew = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error("Title and story are required");
      return;
    }
  
    setSavingAdd(true);
  
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
  
      if (year) formData.append("year", year);
      if (eventDate) formData.append("eventDate", eventDate);
  
      if (imageFiles?.length > 0) {
        imageFiles.forEach((file) => {
          formData.append("images", file);
        });
      }
  
      await axios.post(`${API_URL}/save-family-memory`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
  
      toast.success("Memory saved successfully");
      setAddOpen(false);
  
      // reset inputs
      setTitle("");
      setYear("");
      setEventDate("");
      setDescription("");
      setImageFiles([]);
  
      // Refresh list
      queryClient.invalidateQueries(["familyMemories"]);
  
    } catch (err) {
      console.error("Error saving memory:", err);
      toast.error("Failed to save memory");
    }
  
    setSavingAdd(false);
  };

  const PREVIEW_LIMIT = 260;
  const isLongText = (text) => (text || "").trim().length > PREVIEW_LIMIT;
  const previewText = (text) => {
    const t = (text || "").trim();
    if (t.length <= PREVIEW_LIMIT) return t;
    return `${t.slice(0, PREVIEW_LIMIT)}...`;
  };
  const toggleExpanded = (id) => {
    setExpandedMemories((prev) => ({ ...prev, [id]: !prev[id] }));
  };
   




  /* ===== Edit story ===== */
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editYear, setEditYear] = useState('');
  const [editEventDate, setEditEventDate] = useState('');


const [editMemoryId, setEditMemoryId] = useState(null);
const [savingEdit, setSavingEdit] = useState(false);


const openEdit = (memory) => {
  setEditMemoryId(memory.id);
  setEditTitle(memory.title);
  setEditYear(memory.year || "");
  setEditEventDate(memory.date || "");
  setEditDescription(memory.description);
  setEditOpen(true);
};

  

  const handleEditMemory = async () => {
    if (!editTitle.trim() || !editDescription.trim()) {
      toast.error("Title and story are required");
      return;
    }
  
    setSavingEdit(true);
  
    try {
      await axios.put(`${API_URL}/edit-family-memory/${editMemoryId}`, {
        title: editTitle,
        description: editDescription,
        year: editYear,
        eventDate: editEventDate
      }, { withCredentials: true });
  
      toast.success("Memory updated successfully");
      setEditOpen(false);
  
      queryClient.invalidateQueries(["familyMemories"]);
    } catch (err) {
      console.error("Error updating memory:", err);
      toast.error("Failed to update memory");
    }
  
    setSavingEdit(false);
  };
  


  const handleDeleteMemory = async () => {
    if (!pendingDeleteId) return;
  
    try {
      await axios.delete(`${API_URL}/delete-family-memory/${pendingDeleteId}`, { withCredentials: true });
      toast.success("Memory deleted successfully");
      setDeleteOpen(false);
      setPendingDeleteId(null);
  
      queryClient.invalidateQueries(["familyMemories"]);
    } catch (err) {
      console.error("Error deleting memory:", err);
      toast.error("Failed to delete memory");
    }
  };


  const [imageModalOpen, setImageModalOpen] = useState(false);
const [activeImage, setActiveImage] = useState(null);


  /* ===== Delete story ===== */
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);


  /* ===== Search/filter ===== */
  const filteredMemories = memoryList;



  const filtered = useMemo(() => historyList, [historyList]);

  /* ===== Helpers ===== */
  const displayYear = (item) => {
    if (item.year) return item.year;
    if (item.event_date) {
      const d = new Date(item.event_date);
      if (!isNaN(d)) return d.getFullYear();
    }
    if (item.created_at) {
      const d = new Date(item.created_at);
      if (!isNaN(d)) return d.getFullYear();
    }
    return '';
  };

  const prettyDate = (dStr) => {
    const d = new Date(dStr);
    if (isNaN(d)) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  /* ===== Modal style ===== */
  const modalStyle = {
    overlay: { backgroundColor: 'rgba(0,0,0,.5)', zIndex: 1050 },
    content: {
      inset: '4% auto',
      maxWidth: '92vw',
      width: 680,
      margin: '0 auto',
      borderRadius: 14,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '88vh',
      overflow: 'hidden',
    },
  };

  return (
    <div className="scrollPageContainer theme-light timeline-page">
      <div className="main-wrapper">
        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />
        <ToastContainer position="top-right" autoClose={3000} />

        <div className="page-wrapper">
          <div className="content container-fluid px-0 px-md-1">

            {/* Page header */}
            <div className="vintage-page-header">
              <div className="header-ornament left">
                <svg viewBox="0 0 100 100" width="50" height="50">
                  <path d="M10,50 Q30,20 50,50 T90,50" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              
              <div className="header-content">
                <h1 className="vintage-title">Timeline</h1>
                <p className="vintage-subtitle">Our Family History & Memories</p>
              </div>

              <div className="header-ornament right">
                <svg viewBox="0 0 100 100" width="50" height="50">
                  <path d="M10,50 Q30,20 50,50 T90,50" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
              </div>
            </div>

            {/* Search & Add Section */}
            <div className="vintage-controls">
              {/* Search disabled for now
              <div className="search-box">
                <i className="fas fa-search search-icon" />
                <input
                  className="vintage-search-input"
                  placeholder="Search memories..."
                />
              </div>
              */}
              <button className="vintage-add-btn top-add-btn" onClick={() => setAddOpen(true)}>
                <i className="fas fa-plus-circle me-2" />
                Add Memory
              </button>
            </div>



            {filteredMemories.length === 0 ? (
  <div className="empty-state">
    <div className="empty-icon">
      <i className="fas fa-book-open" />
    </div>
    <h3 className="empty-title">No Memories Yet</h3>
    <p className="empty-text">Start documenting your family's journey</p>
    <button className="vintage-add-btn" onClick={() => setAddOpen(true)}>
      <i className="fas fa-plus-circle me-2" />
      Create First Memory
    </button>
  </div>
) : (
  <div className="vintage-timeline-v2">
    
    <div className="timeline-ornament top">
      <div className="ornament-flourish"></div>
    </div>

    <div className="timeline-spine"></div>

    {filteredMemories.map((item, idx) => {
      const yearDisplay = item.year || "—";
      const eventDate = item.date || item.eventDate || null;

      return (
        <div 
          key={item.id} 
          className="timeline-entry" 
          style={{ animationDelay: `${idx * 0.12}s` }}
        >
          <div className="year-medallion">
            <div className="medallion-outer">
              <div className="medallion-inner">
                <span className="year-text">{yearDisplay}</span>
              </div>
            </div>
            <div className="medallion-shadow"></div>
          </div>

          <div className="connecting-branch">
            <div className="branch-line"></div>
            <div className="branch-dot"></div>
          </div>

          <div className="memory-card">
            <div className="card-corner tl"></div>
            <div className="card-corner tr"></div>
            <div className="card-corner bl"></div>
            <div className="card-corner br"></div>

            <div className="card-header">
              <div className="card-title-section">
                <h3 className="memory-title">{item.title}</h3>

                {eventDate && (
                  <div className="memory-date">
                    <i className="far fa-calendar-alt me-1" />
                    {eventDate}
                  </div>
                )}
              </div>

              <div className="card-buttons">
                <button 
                  className="icon-btn edit" 
                  onClick={() => openEdit(item)}
                  title="Edit"
                >
                  <i className="fas fa-pen" />
                </button>
                <button 
                  className="icon-btn delete"
                  onClick={() => { 
                    setPendingDeleteId(item.id); 
                    setDeleteOpen(true); 
                  }}
                  title="Delete"
                >
                  <i className="fas fa-trash-alt" />
                </button>
          </div>

            </div>

            <div className="card-body">
              <div className="description-wrapper">
                <p
                  className={`memory-description ${expandedMemories[item.id] ? '' : 'is-clamped'}`}
                >
                  {item.description}
                </p>
                {!expandedMemories[item.id] && isLongText(item.description) && (
                  <div className="description-fade" />
                )}
              </div>
              {isLongText(item.description) && (
                <button
                  type="button"
                  className="read-more-btn"
                  onClick={() => toggleExpanded(item.id)}
                >
                  <i className={`fas fa-chevron-${expandedMemories[item.id] ? 'up' : 'down'}`} />
                  {expandedMemories[item.id] ? 'Read less' : 'Read more'}
                </button>
              )}

              {item.images?.length > 0 && (
                <div className="memory-gallery">
                  {item.images.map((img, i) => (
                    <div className="gallery-item polaroid" key={`img-${i}`}>
                      <div className="polaroid-frame">
                        <img 
                          src={`${API_URL}/images/${img.image_name}`}
                          alt="memory" 
                          className="gallery-image"
                          onClick={() => {
                            setActiveImage(`${API_URL}/images/${img.image_name}`);
                            setImageModalOpen(true);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    })}

    <div className="timeline-ornament bottom">
      <div className="ornament-flourish"></div>
    </div>
  </div>
)}

            


<div className='row end-page-space' >  </div>

          </div>
        </div>
      </div>

      {/* Add Story Modal */}
      <ReactModal isOpen={addOpen} onRequestClose={() => setAddOpen(false)} style={modalStyle}>
        <div className="modal-header-vintage">
          <h5 className="modal-title-vintage">Add Family Memory</h5>
          <button className="btn-close-vintage" onClick={() => setAddOpen(false)}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', padding: '1.25rem 1.4rem' }}>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="vintage-label">
                <i className="fas fa-bookmark" style={{opacity:0.6}} />
                Title <span className="required-star">*</span>
              </label>
              <input
                className="vintage-input"
                placeholder="e.g. Grand Family Reunion"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="vintage-label">
                <i className="fas fa-hashtag" style={{opacity:0.6}} />
                Year
              </label>
              <input
                className="vintage-input"
                placeholder="e.g. 2020"
                value={year}
                onChange={(e) => setYear(e.target.value.replace(/[^\d-]/g,''))}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="vintage-label">
                <i className="far fa-calendar-alt" style={{opacity:0.6}} />
                Date
              </label>
              <input
                type="date"
                className="vintage-input"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-3">
            <label className="vintage-label">
              <i className="fas fa-feather-alt" style={{opacity:0.6}} />
              Story <span className="required-star">*</span>
            </label>
            <textarea
              className="vintage-textarea"
              rows={5}
              placeholder="Share your memories, stories, and moments..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="row g-4 mt-2">
            <div className="col-12 col-md-6">
              <div className="vintage-upload-box" onClick={triggerImagePicker}>
                <div className="upload-icon photo">
                  <i className="fas fa-images" />
                </div>
                <div>
                  <div className="upload-title">Add Photos</div>
                  <div className="upload-hint">Capture the moments</div>
                </div>
                <input 
                  ref={imageInputRef} 
                  type="file" 
                  accept="image/*" 
                  multiple 
                  className="d-none" 
                  onChange={handleChooseImages} 
                />
              </div>

              {!!imagePreviews.length && (
                <div className="mt-3">
                  <div className="preview-grid">
                    {imagePreviews.map((src, i) => (
                      <div className="preview-item" key={`pre-img-${i}`}>
                        <img src={src} alt="preview" />
                        <button 
                          className="preview-remove" 
                          onClick={() => removeImageAt(i)}
                        >
                          <i className="fas fa-times" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button 
                    className="btn-clear-uploads" 
                    onClick={clearAllImages}
                  >
                    Clear All Photos
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer-vintage">
          <button className="btn-cancel" onClick={() => setAddOpen(false)}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSaveNew} disabled={savingAdd}>
            {savingAdd ? (
              <>
                <i className="fas fa-spinner fa-spin me-2" />
                Saving...
              </>
            ) : (
              'Save Memory'
            )}
          </button>
        </div>
      </ReactModal>

      {/* Edit Modal */}
      <ReactModal isOpen={editOpen} onRequestClose={() => setEditOpen(false)} style={modalStyle}>
        <div className="modal-header-vintage">
          <h5 className="modal-title-vintage">Edit Memory</h5>
          <button className="btn-close-vintage" onClick={() => setEditOpen(false)}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', padding: '1.1rem 1.2rem' }}>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="vintage-label">
                Title <span style={{color: 'red'}}>*</span>
              </label>
              <input 
                className="vintage-input" 
                value={editTitle} 
                onChange={(e) => setEditTitle(e.target.value)} 
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="vintage-label">Year (optional)</label>
              <input 
                className="vintage-input" 
                value={editYear} 
                onChange={(e) => setEditYear(e.target.value.replace(/[^\d-]/g,''))} 
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="vintage-label">Date (optional)</label>
              <input
                type="date"
                className="vintage-input"
                value={editEventDate}
                onChange={(e) => setEditEventDate(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-3">
            <label className="vintage-label">
              Story <span style={{color: 'red'}}>*</span>
            </label>
            <textarea 
              className="vintage-textarea" 
              rows={6} 
              value={editDescription} 
              onChange={(e) => setEditDescription(e.target.value)} 
            />
          </div>
        </div>
        <div className="modal-footer-vintage">
          <button className="btn-cancel" onClick={() => setEditOpen(false)}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleEditMemory}>
            Update Memory
          </button>
        </div>
      </ReactModal>

      {/* Delete Modal */}
      <ReactModal isOpen={deleteOpen} onRequestClose={() => setDeleteOpen(false)} style={modalStyle}>
        <div className="modal-header-vintage">
          <h5 className="modal-title-vintage">Confirm Deletion</h5>
          <button className="btn-close-vintage" onClick={() => setDeleteOpen(false)}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body" style={{ padding: '1.3rem 1.2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', color: '#8b4513', marginBottom: '1rem' }}>
            <i className="fas fa-exclamation-triangle" />
          </div>
          <p style={{ fontSize: '1.1rem', color: '#4a4a4a' }}>
            Are you sure you want to delete this memory? This action cannot be undone.
          </p>
        </div>
        <div className="modal-footer-vintage">
          <button className="btn-cancel" onClick={() => setDeleteOpen(false)}>
            Keep It
          </button>
          <button className="btn-delete" onClick={handleDeleteMemory}>
            Delete Forever
          </button>
        </div>
      </ReactModal>



       {/* Images Modal */}
       <ReactModal isOpen={imageModalOpen}
  onRequestClose={() => setImageModalOpen(false)} style={modalStyle}>
        <div className="modal-header-vintage">
          <h5 className="modal-title-vintage">Memory Image</h5>
          <button className="btn-close-vintage" onClick={() => setImageModalOpen(false)}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', padding: '1.1rem 1.2rem' }}>
          <div className="row g-3">
             
            <div className="col-12 col-md-12">

            {activeImage && (
      
                <img 
                  src={activeImage} 
                  alt="memory full" 
                  style={{ width: '100%', height: 'auto' }}
                />
            )}
               
            </div>
             
          </div>
 
        </div>
        <div className="modal-footer-vintage">
          <button className="btn-cancel" onClick={() => setImageModalOpen(false)}>
            Cancel
          </button>
          
        </div>
      </ReactModal>


      {/* Styles remain the same */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Crimson+Text:ital,wght@0,400;0,600;1,400&display=swap');

        :root {
          /* Vintage newspaper palette */
          --vintage-cream: #f3eadb;
          --vintage-tan: #dccab6;
          --vintage-brown: #7a4a2e;
          --vintage-dark: #3b2f2a;
          --vintage-gold: #c9a656;
          --vintage-red: #a05a3a;
          --paper-texture: #f7efe3;

          /* App green theme */
          --theme-green-dark: #184A2C;
          --theme-green: #2e7d32;
          --theme-green-light: #e6f4ea;
          --theme-green-soft: #cfe7d4;
          --theme-green-ink: #0f3b24;
        }

        .timeline-page .page-wrapper {
          background: var(--theme-green-light);
        }

        /* Override global .end-page-space margin-top:20% for this page */
        .timeline-page .timeline-end-space {
          margin-top: 0 !important;
          height: 8px !important;
        }

        .vintage-page-header {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          padding: 2rem;
          background: var(--theme-green-dark);
          border: 2px solid var(--theme-green-dark);
          border-radius: 8px;
          margin-bottom: 2rem;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12), inset 0 0 40px rgba(255,255,255,0.08);
        }

        .header-ornament {
          color: #dfeee3;
          opacity: 0.7;
        }

        .header-ornament.right svg {
          transform: scaleX(-1);
        }

        .header-content {
          text-align: center;
        }

        .vintage-title {
          font-family: 'Playfair Display', serif;
          font-size: 3rem;
          font-weight: 800;
          color: #ffffff;
          margin: 0;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          text-shadow: 2px 2px 6px rgba(0,0,0,0.25);
        }

        .vintage-subtitle {
          font-family: 'Crimson Text', serif;
          font-size: 1.1rem;
          font-style: italic;
          color: #e6f4ea;
          margin: 0.5rem 0 0 0;
          letter-spacing: 0.05em;
        }

        .vintage-controls {
          display: flex;
          gap: 0.6rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          align-items: center;
          justify-content: flex-start;
          background: var(--theme-green-light);
          border: 1px solid var(--theme-green-soft);
          border-radius: 10px;
          padding: 0.55rem;
        }

        .search-box {
          position: relative;
          flex: 1;
          min-width: 250px;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--theme-green);
          opacity: 0.5;
        }

        .vintage-search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.75rem;
          border: 2px solid var(--theme-green);
          border-radius: 8px;
          background: var(--paper-texture);
          font-family: 'Crimson Text', serif;
          font-size: 1rem;
          color: var(--vintage-dark);
          transition: all 0.3s ease;
        }

        .vintage-search-input:focus {
          outline: none;
          border-color: var(--theme-green-dark);
          box-shadow: 0 0 0 3px rgba(46,125,50,0.18);
        }

        .vintage-add-btn {
          padding: 0.75rem 1.5rem;
          background: var(--theme-green-dark);
          color: white;
          border: none;
          border-radius: 8px;
          font-family: 'Crimson Text', serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(24,74,44,0.3);
          display: flex;
          align-items: center;
          white-space: nowrap;
        }

        .vintage-add-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(24,74,44,0.4);
        }

        .vintage-timeline-v2 {
          position: relative;
          max-width: 1000px;
          margin: 0 auto;
          padding: 0.6rem 0.45rem;
          background: var(--paper-texture);
          border-radius: 8px;
          box-shadow: 
            0 10px 40px rgba(0,0,0,0.15),
            inset 0 0 100px rgba(139,69,19,0.03);
        }

        .timeline-spine {
          position: absolute;
          left: 80px;
          top: 80px;
          bottom: 80px;
          width: 6px;
          background: var(--theme-green);
          box-shadow: 
            0 0 10px rgba(139,69,19,0.3),
            inset 0 0 3px rgba(0,0,0,0.3);
          border-radius: 3px;
        }

        .timeline-ornament {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 100px;
          height: 40px;
        }

        .timeline-ornament.top {
          top: 10px;
        }

        .timeline-ornament.bottom {
          bottom: 10px;
        }

        .ornament-flourish {
          width: 100%;
          height: 100%;
          background: var(--theme-green-soft);
          opacity: 0.3;
          border-radius: 50%;
        }

        .timeline-entry {
          position: relative;
          display: grid;
          grid-template-columns: 66px 42px minmax(0, 1fr);
          gap: 0;
          margin-bottom: 1.5rem;
          opacity: 0;
          animation: entryFadeIn 0.8s ease forwards;
        }

        @keyframes entryFadeIn {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .year-medallion {
          grid-column: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 3;
        }

        .medallion-outer {
          width: 70px;
          height: 70px;
          background: var(--theme-green);
          border-radius: 50%;
          display: grid;
          place-items: center;
          box-shadow: 
            0 6px 20px rgba(139,69,19,0.4),
            inset 0 2px 4px rgba(255,255,255,0.4),
            inset 0 -2px 4px rgba(0,0,0,0.2);
          border: 3px solid var(--vintage-brown);
        }

        .medallion-inner {
          width: 54px;
          height: 54px;
          background: var(--theme-green-light);
          border-radius: 50%;
          display: grid;
          place-items: center;
          border: 2px solid rgba(139,69,19,0.3);
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
        }

        .year-text {
          font-family: 'Playfair Display', serif;
          font-size: 1rem;
          font-weight: 800;
          color: var(--vintage-brown);
          letter-spacing: 0.05em;
        }

        .medallion-shadow {
          position: absolute;
          width: 60px;
          height: 12px;
          background: rgba(0,0,0,0.12);
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          filter: blur(4px);
        }

        .connecting-branch {
          grid-column: 2;
          display: flex;
          align-items: center;
          position: relative;
        }

        .branch-line {
          width: 100%;
          height: 3px;
          background: var(--theme-green);
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }

        .branch-dot {
          position: absolute;
          right: -2px;
          width: 10px;
          height: 10px;
          background: var(--vintage-brown);
          border-radius: 50%;
          border: 2px solid var(--paper-texture);
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }

        .memory-card {
          grid-column: 3;
          background: #ffffff;
          border: 1px solid var(--vintage-tan);
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 
            0 8px 24px rgba(0,0,0,0.12),
            inset 0 1px 0 rgba(255,255,255,0.8);
          position: relative;
          transition: all 0.3s ease;
margin-left: -60px;

          
        }

        .memory-card:hover {
          transform: translateX(8px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.18);
        }

        .card-corner {
          position: absolute;
          width: 20px;
          height: 20px;
          border: 2px solid var(--vintage-gold);
          opacity: 0.4;
        }

        .card-corner.tl {
          top: 8px;
          left: 8px;
          border-right: none;
          border-bottom: none;
        }

        .card-corner.tr {
          top: 8px;
          right: 8px;
          border-left: none;
          border-bottom: none;
        }

        .card-corner.bl {
          bottom: 8px;
          left: 8px;
          border-right: none;
          border-top: none;
        }

        .card-corner.br {
          bottom: 8px;
          right: 8px;
          border-left: none;
          border-top: none;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 2px dotted var(--vintage-tan);
        }

        .card-title-section {
          flex: 1;
        }

        .memory-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--vintage-dark);
          margin: 0 0 0.5rem 0;
          line-height: 1.3;
        }

        .memory-date {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-family: 'Crimson Text', serif;
          font-size: 0.9rem;
          color: var(--vintage-red);
          background: rgba(139,69,19,0.08);
          padding: 0.3rem 0.75rem;
          border-radius: 20px;
          border: 1px solid rgba(139,69,19,0.15);
        }

        .card-buttons {
          display: flex;
          gap: 0.5rem;
        }

      .icon-btn {
  width: auto;
  height: auto;
  border: none;
  background: transparent;
  border-radius: 0;
  display: grid;
  place-items-center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  padding: 0.5rem;
}
        .icon-btn.edit {
          color: #2d7a3e;
        }

        .icon-btn.edit:hover {
          background: #2d7a3e;
          color: white;
          border-color: #2d7a3e;
          transform: scale(1.1) rotate(5deg);
        }

        .icon-btn.delete {
          color: #a0522d;
        }

        .icon-btn.delete:hover {
          background: #a0522d;
          color: white;
          border-color: #a0522d;
          transform: scale(1.1) rotate(-5deg);
        }

        .card-body {
          margin-top: 1rem;
        }

        .memory-description {
          font-family: 'Crimson Text', serif;
          font-size: 1.05rem;
          line-height: 1.8;
          color: #4a4a4a;
          margin: 0;
          white-space: pre-wrap;
          word-break: break-word;
          overflow-wrap: anywhere;
        }

        .description-wrapper {
          position: relative;
          margin-bottom: 0.6rem;
        }

        .memory-description.is-clamped {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .description-fade {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2.8rem;
          background: linear-gradient(to bottom, transparent, #ffffff);
          pointer-events: none;
        }

        .read-more-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          margin-top: 0.4rem;
          border: 1px solid rgba(122,74,46,0.28);
          background: rgba(122,74,46,0.06);
          color: var(--vintage-brown);
          font-family: 'Crimson Text', serif;
          font-size: 0.9rem;
          font-weight: 700;
          padding: 0.28rem 0.9rem;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.2s ease;
          letter-spacing: 0.02em;
        }

        .read-more-btn i {
          font-size: 0.75rem;
          transition: transform 0.2s ease;
        }

        .read-more-btn:hover {
          background: rgba(122,74,46,0.13);
          border-color: var(--vintage-brown);
          transform: translateY(-1px);
        }

        .memory-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 1rem;
          margin-top: 1.5rem;
        }

        .gallery-item {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          aspect-ratio: 4/3;
        }

        .gallery-item.polaroid {
          aspect-ratio: 1;
          background: white;
          padding: 0.5rem;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          transform: rotate(-1deg);
          transition: transform 0.3s ease;
        }

        .gallery-item.polaroid:nth-child(even) {
          transform: rotate(1deg);
        }

        .gallery-item.polaroid:hover {
          transform: rotate(0deg) scale(1.05);
          z-index: 10;
        }

        .polaroid-frame {
          width: 100%;
          height: 85%;
          overflow: hidden;
          background: #f0f0f0;
        }

        .polaroid-frame img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          filter: sepia(0.2) contrast(1.1) brightness(0.98);
        }

        .gallery-item img,
        .video-player {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--vintage-cream);
          border: 3px dashed var(--vintage-tan);
          border-radius: 12px;
        }

        .empty-icon {
          font-size: 4rem;
          color: var(--vintage-brown);
          opacity: 0.4;
          margin-bottom: 1rem;
        }

        .empty-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.8rem;
          color: var(--vintage-dark);
          margin-bottom: 0.5rem;
        }

        .empty-text {
          font-family: 'Crimson Text', serif;
          font-size: 1.1rem;
          font-style: italic;
          color: var(--vintage-red);
          margin-bottom: 2rem;
        }

        .modal-header-vintage {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          background: var(--theme-green-dark);
          border-bottom: 3px solid var(--theme-green-ink);
        }

        .modal-title-vintage {
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .btn-close-vintage {
          width: 32px;
          height: 32px;
          border: none;
          background: rgba(255,255,255,0.18);
          color: white;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s ease;
          display: grid;
          place-items-center;
        }

        .btn-close-vintage:hover {
          background: rgba(255,255,255,0.3);
          transform: rotate(90deg);
        }

        .vintage-label {
          font-size: 0.78rem;
          font-weight: 700;
          color: #184A2C;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          margin-bottom: 0.45rem;
          display: flex;
          align-items: center;
          gap: 0.35rem;
        }

        .vintage-label .required-star {
          color: #c0392b;
          font-size: 0.9rem;
          line-height: 1;
        }

        .vintage-input,
        .vintage-textarea {
          width: 100%;
          padding: 0.78rem 1rem;
          border: 1.5px solid #bdd0be;
          border-radius: 8px;
          background: #ffffff;
          font-family: 'Crimson Text', serif;
          font-size: 1.05rem;
          color: #1c2b1f;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
          caret-color: #184A2C;
        }

        .vintage-input::placeholder,
        .vintage-textarea::placeholder {
          color: #9ab8a0;
          font-style: italic;
          font-weight: 400;
        }

        .vintage-input:focus,
        .vintage-textarea:focus {
          outline: none;
          border-color: #184A2C;
          box-shadow: inset 3px 0 0 #184A2C, 0 0 0 3px rgba(24,74,44,0.1), 0 2px 8px rgba(0,0,0,0.06);
          background: #fafffe;
        }

        .vintage-input:hover:not(:focus),
        .vintage-textarea:hover:not(:focus) {
          border-color: #88aa8a;
        }

        input[type="date"].vintage-input {
          cursor: pointer;
        }

        .vintage-textarea {
          resize: vertical;
          min-height: 120px;
          line-height: 1.7;
        }

        .vintage-upload-box {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem;
          border: 2px dashed var(--theme-green-soft);
          border-radius: 10px;
          background: rgba(245,235,224,0.3);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .vintage-upload-box:hover {
          border-color: var(--theme-green);
          background: rgba(245,235,224,0.5);
          transform: translateY(-2px);
        }

        .upload-icon {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          display: grid;
          place-items-center;
          font-size: 1.5rem;
        }

        .upload-icon.photo {
          background: rgba(46,125,50,0.18);
          color: var(--theme-green-dark);
        }

        .upload-icon.video {
          background: rgba(46,125,50,0.18);
          color: var(--theme-green);
        }

        .upload-title {
          font-family: 'Crimson Text', serif;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--vintage-dark);
        }

        .upload-hint {
          font-family: 'Crimson Text', serif;
          font-size: 0.9rem;
          font-style: italic;
          color: var(--theme-green);
          opacity: 0.8;
        }

        .preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 0.75rem;
        }

        .preview-item {
          position: relative;
          aspect-ratio: 1;
          border-radius: 8px;
          overflow: hidden;
          background: #f0f0f0;
          border: 2px solid var(--theme-green-soft);
        }

        .preview-item img,
        .preview-item video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .preview-remove {
          position: absolute;
          top: 4px;
          right: 4px;
          width: 26px;
          height: 26px;
          border: none;
          background: rgba(0,0,0,0.7);
          color: white;
          border-radius: 50%;
          cursor: pointer;
          display: grid;
          place-items-center;
          transition: all 0.2s ease;
        }

        .preview-remove:hover {
          background: var(--vintage-red);
          transform: scale(1.1);
        }

        .btn-clear-uploads {
          margin-top: 0.75rem;
          padding: 0.5rem 1rem;
          background: transparent;
          border: 2px solid var(--theme-green);
          color: var(--theme-green-dark);
          border-radius: 6px;
          font-family: 'Crimson Text', serif;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-clear-uploads:hover {
          background: var(--theme-green-soft);
        }

        .modal-footer-vintage {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 2px solid var(--theme-green-soft);
          background: var(--paper-texture);
        }

        .btn-cancel,
        .btn-save,
        .btn-delete {
          padding: 0.65rem 1.5rem;
          border: 2px solid;
          border-radius: 6px;
          font-family: 'Crimson Text', serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-cancel {
          background: white;
          border-color: var(--theme-green-soft);
          color: var(--theme-green-dark);
        }

        .btn-cancel:hover {
          background: var(--theme-green-soft);
        }

        .btn-save {
          background: var(--theme-green-dark);
          border-color: var(--theme-green);
          color: white;
        }

        .btn-save:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(24,74,44,0.3);
        }

        .btn-save:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-delete {
          background: #1e5a2d;
          border-color: #2d7a3e;
          color: white;
        }

        .btn-delete:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(24,74,44,0.35);
        }

        @media (max-width: 768px) {
          .vintage-title {
            font-size: 2rem;
          }

          .timeline-spine {
            left: 20px;
          }

          .timeline-entry {
            grid-template-columns: 50px 40px 1fr;
            margin-bottom: 3rem;
          }

          .medallion-outer {
            width: 30px;
            height: 30px;
          }

          .medallion-inner {
            width: 28px;
            height: 28px;
          }

          .year-text {
            font-size: 0.8rem;
          }

          .memory-title {
            font-size: 1.2rem;
          }




/* ====== COMPACT MODE OVERRIDES ====== */

/* Page header smaller */
.vintage-page-header{
  padding: 1.1rem !important;
  gap: 1rem !important;
  margin-bottom: 1.2rem !important;
}

.vintage-title{
  font-size: 2.1rem !important;
  letter-spacing: 0.07em !important;
}

.vintage-subtitle{
  font-size: 0.95rem !important;
  margin-top: 0.25rem !important;
}

/* Controls tighter */
.vintage-controls{
  gap: 0.75rem !important;
  margin-bottom: 1.2rem !important;
}

.vintage-search-input{
  padding: 0.6rem 0.9rem 0.6rem 2.4rem !important;
  font-size: 0.95rem !important;
}

.vintage-add-btn{
  padding: 0.6rem 1rem !important;
  font-size: 0.95rem !important;
}
.top-add-btn{
  padding: 0.45rem 0.8rem !important;
  font-size: 0.86rem !important;
  border-radius: 9px !important;
}

/* Force theme consistency on mobile/merged CSS edge-cases */
.timeline-page .vintage-page-header{
  background: #184A2C !important;
  border-color: #184A2C !important;
}
.timeline-page .vintage-add-btn{
  background: #184A2C !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(24,74,44,.28) !important;
}
.timeline-page .vintage-add-btn i{
  color: #ffffff !important;
}
.timeline-page .btn-save{
  background: #184A2C !important;
  border-color: #2e7d32 !important;
  color: #fff !important;
}
.timeline-page .btn-delete{
  background: #1e5a2d !important;
  border-color: #2d7a3e !important;
  color: #fff !important;
}

/* Timeline container less padding */
.vintage-timeline-v2{
  padding: 0.75rem !important;
}

/* Spine slightly closer */
.timeline-spine{
  left: 70px !important;
  top: 60px !important;
  bottom: 60px !important;
  width: 5px !important;
}

/* Each entry tighter */
.timeline-entry{
  grid-template-columns: 70px 50px 1fr !important;
  margin-bottom: 2.2rem !important;
}

/* Year medallion smaller */
.medallion-outer{
  width: 56px !important;
  height: 56px !important;
}
.medallion-inner{
  width: 42px !important;
  height: 42px !important;
}
.year-text{
  font-size: 0.9rem !important;
}

/* Card smaller padding & text */
.memory-card{
  padding: 1rem !important;
  border-radius: 10px !important;
  margin-left: -18px !important;
}

.memory-title{
  font-size: 1.25rem !important;
  margin-bottom: 0.35rem !important;
}

.memory-description{
  font-size: 0.98rem !important;
  line-height: 1.65 !important;
  margin: 0 0 0.9rem 0 !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
}

/* Header section inside card tighter */
.card-header{
  margin-bottom: 0.75rem !important;
  padding-bottom: 0.75rem !important;
}

/* Gallery smaller */
.memory-gallery{
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)) !important;
  gap: 0.75rem !important;
  margin-top: 1rem !important;
}

/* Polaroids smaller */
.gallery-item.polaroid{
  padding: 0.35rem !important;
}

/* Empty state smaller */
.empty-state{
  padding: 2.5rem 1.5rem !important;
}
.empty-title{
  font-size: 1.5rem !important;
}
.empty-text{
  font-size: 1rem !important;
}

/* ===== Mobile extra compact ===== */
@media (max-width: 768px){
  .timeline-page .vintage-controls{
    padding: 0.45rem !important;
    margin-bottom: 0.9rem !important;
  }

  .timeline-page .timeline-spine,
  .timeline-page .year-medallion,
  .timeline-page .connecting-branch,
  .timeline-page .timeline-ornament{
    display: none !important;
  }

  .timeline-page .timeline-entry{
    display: block !important;
    margin-bottom: 1rem !important;
  }

  .timeline-page .vintage-timeline-v2{
    padding: 0.45rem !important;
  }

  .timeline-page .memory-card{
    margin-left: 0 !important;
    width: 100% !important;
    padding: 0.8rem !important;
  }

  .timeline-page .memory-description{
    font-size: 0.94rem !important;
    line-height: 1.55 !important;
  }

  .vintage-page-header{
    padding: 0.9rem !important;
    gap: 0.75rem !important;
  }

  .vintage-title{
    font-size: 1.6rem !important;
  }

  .memory-gallery{
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)) !important;
  }
}









          .memory-gallery {
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          }
        }
      `}</style>
    </div>
  );
}
